/**
 * Mobile Phase 2, chunk 1: the feed, announcements, acknowledgements and kudos. Keys match the web pages'
 * (['home', ws, 'feed', …], ['knowledge', ws, …], ['kudos', ws, …], ['acks']) so invalidations are shared.
 */
import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { FeedFilters, KudosBadge } from '@tradeoutlet/shared';
import { dataApi } from './client';

type Ws = string | null | undefined;

export const feedKeys = {
  feed: (ws: Ws, f: FeedFilters) => ['home', ws, 'feed', f.types.join(','), f.scope] as const,
  head: (ws: Ws, f: FeedFilters) => ['home', ws, 'feed-head', f.types.join(','), f.scope] as const,
  announcements: (ws: Ws, q = '') => ['knowledge', ws ?? '', 'ann', 'feed', q] as const,
  announcement: (ws: Ws, id: string) => ['knowledge', ws ?? '', 'ann-one', id] as const,
};
/** Everything an acknowledgement or a new post can change: the feed, announcement lists, Today and the acks badge. */
export function invalidateNews(qc: QueryClient, ws: Ws) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['home', ws] }), qc.invalidateQueries({ queryKey: ['knowledge', ws ?? ''] }),
    qc.invalidateQueries({ queryKey: ['acks'] }), qc.invalidateQueries({ queryKey: ['kudos', ws ?? ''] }),
    qc.invalidateQueries({ queryKey: ['wf', ws, 'me-today'] }), qc.invalidateQueries({ queryKey: ['nav-badges', ws ?? ''] }),
  ]);
}

/** The full feed, 15 at a time, newest first; pinned posts come with the first page. */
export function useHomeFeed(ws: Ws, f: FeedFilters, o: { keep?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: feedKeys.feed(ws, f),
    queryFn: ({ pageParam }) => dataApi().home.feed(ws!, { types: f.types, scope: f.scope, before: pageParam ?? undefined, limit: 15 }),
    initialPageParam: null as string | null,
    getNextPageParam: last => last.next_before,
    enabled: Boolean(ws),
    // t184u: keep every loaded page while an item is open (Back restores the same cards and scroll position).
    // t298u: coming back (`keep`) shows the cached pages as they were, and a focus never refetches them: either could
    // reorder the cards under you. New activity waits behind the "N new posts" pill (the head query).
    gcTime: 30 * 60_000, staleTime: 60_000, refetchOnMount: !o.keep, refetchOnWindowFocus: false,
  });
}
/**
 * t277u: the previous feed visit, for the "Earlier · already seen" divider (desktop and phone Home). Opening the
 * feed records a visit on the server and keeps the previous one for this visit. Coming back within 30 minutes (Back
 * from a card, switching tabs) is the same visit, so the divider stays put. Leaving after a while records the visit
 * again, so what you saw this time is not "new" next time.
 */
const VISIT_WINDOW = 30 * 60_000;
const visits = new Map<string, { at: number; since?: string | null; promise: Promise<string | null> }>();
export function useFeedVisit(ws: Ws): string | null {
  const [since, setSince] = useState<string | null>(() => {
    const c = ws ? visits.get(ws) : undefined;
    return c && Date.now() - c.at < VISIT_WINDOW ? c.since ?? null : null;
  });
  useEffect(() => {
    if (!ws) return;
    let c = visits.get(ws);
    if (!c || Date.now() - c.at >= VISIT_WINDOW) {
      const entry: { at: number; since?: string | null; promise: Promise<string | null> } = { at: Date.now(), promise: Promise.resolve(null) };
      entry.promise = Promise.resolve().then(() => dataApi().home.feedVisit(ws)).then(r => { entry.since = r.previous_visit_at; return r.previous_visit_at; },
        () => { visits.delete(ws); return null; });
      visits.set(ws, entry);
      c = entry;
    }
    let live = true;
    void c.promise.then(v => { if (live) setSince(v); });
    const opened = Date.now();
    return () => {
      live = false;
      // Stamp the end of a real visit (not React's dev double-mount), so what you saw isn't "new" next time.
      if (Date.now() - opened > 5_000) void Promise.resolve().then(() => dataApi().home.feedVisit(ws)).catch(() => undefined);
    };
  }, [ws]);
  return since;
}
/** For tests: forget remembered visits. */
export function resetFeedVisits() { visits.clear(); }

/** The head of the feed, checked every minute and on focus, for the "N new posts" pill. */
export function useHomeFeedHead(ws: Ws, f: FeedFilters, enabled: boolean) {
  return useQuery({ queryKey: feedKeys.head(ws, f), queryFn: () => dataApi().home.feed(ws!, { types: f.types, scope: f.scope, limit: 15 }), enabled: Boolean(ws) && enabled, refetchInterval: 60_000, refetchOnWindowFocus: true });
}

export function useAnnouncements(ws: Ws, q = '') {
  return useQuery({ queryKey: feedKeys.announcements(ws, q), queryFn: () => dataApi().announcements.list(ws!, { view: 'feed', q: q || undefined }), enabled: Boolean(ws) });
}
export function useAnnouncement(ws: Ws, id: string | undefined) {
  return useQuery({ queryKey: feedKeys.announcement(ws, id ?? ''), queryFn: () => dataApi().announcements.get(ws!, id!), enabled: Boolean(ws && id) });
}
/** Acknowledge an announcement (the current version). */
export function useAckAnnouncement(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => dataApi().announcements.ack(ws!, id), onSettled: () => invalidateNews(qc, ws) });
}
/** Acknowledge a chat message that asks for it (the feed's acknowledgement cards). */
export function useAckMessage(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (messageId: string) => dataApi().acknowledge(messageId), onSettled: () => invalidateNews(qc, ws) });
}

export interface GiveKudosInput { body: string; badge?: KudosBadge; recipient_ids: string[]; team_ids: string[]; attachment_ids: string[] }
export function useGiveKudos(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: GiveKudosInput) => dataApi().createKudos(ws!, { ...b, body: b.body.trim() }), onSuccess: () => invalidateNews(qc, ws) });
}
export function useReactKudos(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: string; emoji: string; mine: boolean }) => (a.mine ? dataApi().unreactKudos(a.id, a.emoji) : dataApi().reactKudos(a.id, a.emoji)),
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: ['home', ws] }), qc.invalidateQueries({ queryKey: ['kudos', ws ?? ''] })]),
  });
}

/** Answer a shift offered to you from its feed card. */
export function useRespondShiftOffer(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { id: string; action: 'accept' | 'decline' }) => dataApi().wf.respondShiftOffer(ws!, a.id, a.action),
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: ['home', ws] }), qc.invalidateQueries({ queryKey: ['wf', ws] })]),
  });
}
export type Rsvp = 'accepted' | 'tentative' | 'declined';
/** Going / Maybe / Can't go on an event card. */
export function useRsvpEvent(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (a: { id: string; rsvp: Rsvp }) => dataApi().rsvpEvent(a.id, a.rsvp), onSettled: () => qc.invalidateQueries({ queryKey: ['home', ws] }) });
}
